import { Link } from "react-router-dom";
import { Trophy, Zap, Target, Users, TrendingUp, Award, Flame, Rocket, Check, Megaphone, Briefcase, ShieldCheck, Wand2, Sparkles, ArrowRight, CreditCard, Clock, Menu, MessagesSquare, BarChart3, Swords, LayoutGrid } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import TrialCountdown from "@/components/TrialCountdown";
import FeaturePanel from "@/components/FeaturePanel";
import { useTrialWindow } from "@/hooks/useTrialWindow";

const BLUE = "#1E4FFF";
const GREEN = "#16A34A";
const INK = "#0B0B12";
const GOLD = "#FFC832";

function PromoBanner({ cutoffIso }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);
  if (!cutoffIso) return null;
  const ms = Math.max(0, new Date(cutoffIso).getTime() - Date.now());
  if (ms <= 0) return null;
  const s = Math.floor(ms / 1000);
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return (
    <div
      className="mb-10 rounded-2xl px-5 sm:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-5 text-white shadow-[0_24px_50px_-24px_rgba(22,163,74,0.6)]"
      style={{ background: "linear-gradient(100deg,#16A34A 0%,#1E4FFF 100%)" }}
      data-testid="pricing-promo-banner"
    >
      <div className="flex items-center gap-4 text-center sm:text-left">
        <span className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: "rgba(255,255,255,0.18)" }}>
          <Flame className="w-6 h-6" style={{ color: GOLD }} />
        </span>
        <div>
          <div className="font-label tracking-[0.2em] text-xs opacity-90">LIMITED TIME OFFER</div>
          <div className="font-display text-2xl sm:text-3xl leading-tight">30-DAY FREE TRIAL</div>
          <div className="text-sm opacity-90 mt-0.5">Then $74.99/wk. Cancel anytime.</div>
        </div>
      </div>
      <div className="flex items-center gap-4 flex-shrink-0">
        {days <= 365 ? (
          <div className="text-center">
            <div className="font-mono font-bold text-lg sm:text-2xl tabular-nums leading-none">{days}d {pad(hours)}:{pad(minutes)}:{pad(seconds)}</div>
            <div className="font-label text-[10px] tracking-[0.15em] opacity-80 mt-1">OFFER ENDS IN</div>
          </div>
        ) : null}
        <div className="flex flex-col items-center gap-1.5">
          <Link to="/signup" data-testid="pricing-promo-cta">
            <Button className="font-label rounded-lg h-11 px-5 whitespace-nowrap" style={{ backgroundColor: "#FFFFFF", color: GREEN }}>Claim offer →</Button>
          </Link>
          <div className="flex items-center gap-1 text-[11px] opacity-90 text-center" data-testid="promo-founders-badge-note">
            <Sparkles className="w-3 h-3" style={{ color: GOLD }} />
            <span>Cancel anytime before your trial ends</span>
          </div>
        </div>
      </div>
    </div>
  );
}


const SCREENS = {
  hq: "/store/screenshots/01-hq.png",
  pit: "/store/screenshots/02-the-pit.png",
  rankings: "/store/screenshots/03-rankings.png",
  arena: "/store/screenshots/04-arena.png",
  more: "/store/screenshots/05-more.png",
};

const BOARDROOM = "https://images.unsplash.com/photo-1787684047991-0241318b066a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

function Phone({ src, className = "", style }) {
  return (
    <div className={`relative ${className}`} style={{ containerType: "inline-size", ...style }}>
      {/* side buttons */}
      <div className="absolute -left-[1.5px] top-[19%] w-[2px] h-6 rounded-l-[2px] bg-[#8f8f96]" />
      <div className="absolute -left-[1.5px] top-[29%] w-[2px] h-9 rounded-l-[2px] bg-[#8f8f96]" />
      <div className="absolute -left-[1.5px] top-[41%] w-[2px] h-9 rounded-l-[2px] bg-[#8f8f96]" />
      <div className="absolute -right-[1.5px] top-[25%] w-[2px] h-14 rounded-r-[2px] bg-[#8f8f96]" />
      {/* outer titanium rail — bright metallic edge */}
      <div
        className="relative rounded-[13cqw] p-[2.2cqw] shadow-[0_28px_60px_-22px_rgba(11,11,18,0.55)]"
        style={{ backgroundImage: "linear-gradient(108deg, #58585f 0%, #b7b7bd 9%, #ededf1 17%, #9a9aa1 28%, #55555b 40%, #7e7e85 50%, #4f4f55 60%, #a9a9b0 72%, #ededf1 82%, #86868d 91%, #4c4c52 100%)" }}
      >
        {/* inner dark rim */}
        <div className="rounded-[11cqw] bg-gradient-to-b from-[#2b2b31] via-[#161619] to-[#08080a] p-[2.4cqw]">
          <div className="relative overflow-hidden rounded-[8cqw] bg-black">
            {/* status bar */}
            <div className="relative w-full bg-black flex items-center justify-center py-[2.6%]">
              {/* dynamic island */}
              <div className="h-[3.6cqw] w-[26%] max-w-[80px] bg-black rounded-full" />
            </div>
            <img src={src} alt="" className="w-full block object-cover" />
          </div>
        </div>
      </div>
    </div>
  );
}

function FeatureRow({ icon, title, sub, desc }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8 items-start py-7 border-t border-[#E9EBF0] first:border-t-0">
      <div className="flex items-start gap-4">
        <span className="flex-shrink-0 mt-1" style={{ color: BLUE }}>{icon}</span>
        <div className="font-display leading-none">
          <div className="text-lg sm:text-xl tracking-tight" style={{ color: INK }}>{title}</div>
          {sub && <div className="text-lg sm:text-xl tracking-tight mt-1" style={{ color: INK }}>{sub}</div>}
        </div>
      </div>
      <p className="text-[15px] leading-relaxed text-[#4B5563] sm:pl-8 sm:border-l sm:border-[#E9EBF0]">{desc}</p>
    </div>
  );
}

export default function Landing() {
  const [plans, setPlans] = useState([]);
  const { trialOpen, cutoffIso } = useTrialWindow();

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-white text-[#0B0B12] relative overflow-hidden antialiased">
      {/* Sticky announcement bar + nav */}
      <div className="sticky top-0 z-50">
        <TrialCountdown />
        <nav className="bg-white/85 backdrop-blur-xl border-b border-[#EDEFF3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="brand-logo">
            <img src="/app-icon.png" alt="SalesArena HQ" className="w-9 h-9 rounded-lg object-cover flex-shrink-0" data-testid="brand-logo-img" />
            <span className="font-logo text-lg sm:text-xl tracking-tight whitespace-nowrap" style={{ color: INK }}>SALESARENA<span style={{ color: BLUE }}>HQ</span></span>
          </Link>
          <div className="hidden md:flex items-center gap-8 font-label">
            <a href="#features" className="text-[#4B5563] hover:text-black transition-colors" data-testid="nav-features">Features</a>
            <a href="#pricing" className="text-[#4B5563] hover:text-black transition-colors" data-testid="nav-pricing">Pricing</a>
            <a href="#how" className="text-[#4B5563] hover:text-black transition-colors" data-testid="nav-how">How it works</a>
          </div>
          <div className="flex items-center gap-1 sm:gap-3">
            <Link to="/login" data-testid="nav-login">
              <Button variant="ghost" className="text-[#4B5563] hover:text-black hover:bg-transparent font-label px-2 sm:px-4">Log in</Button>
            </Link>
            <Link to="/signup" data-testid="nav-signup">
              <Button className="text-white font-label rounded-lg h-10 px-4 sm:px-5 whitespace-nowrap" style={{ backgroundColor: BLUE }}>{trialOpen ? "Start Free" : "Get Started"}</Button>
            </Link>
          </div>
        </div>
        </nav>
      </div>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 pt-8 sm:pt-20 pb-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-center">
          <div className="animate-fade-up">
            <div className="font-label tracking-[0.2em] mb-4 text-xs sm:text-sm" style={{ color: BLUE }}>THE ALL-IN-ONE PERFORMANCE PLATFORM</div>
            <h1 className="font-display leading-[0.92] tracking-tight text-4xl sm:text-6xl lg:text-7xl">
              <span style={{ color: INK }}>TURN YOUR SALES FLOOR INTO A</span>{" "}
              <span style={{ color: BLUE }}>CHAMPIONSHIP ARENA.</span>
            </h1>
            <div className="mt-5 h-1 w-16 rounded-full" style={{ backgroundColor: BLUE }} />
            <p className="mt-5 text-base sm:text-lg text-[#4B5563] max-w-xl leading-relaxed">
              SalesArena is the all-in-one performance platform that turns your sales team into champions.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Link to="/signup" data-testid="hero-cta-signup">
                <Button size="lg" className="text-white font-label h-14 px-8 rounded-lg w-full sm:w-auto group" style={{ backgroundColor: BLUE }}>
                  {trialOpen ? "Start free trial" : "Choose your plan"} <ArrowRight className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
              <a href="#pricing" data-testid="hero-cta-pricing">
                <Button size="lg" variant="outline" className="border-[#D5D9E2] text-[#0B0B12] hover:bg-[#F4F6FA] font-label h-14 px-8 rounded-lg w-full sm:w-auto">See pricing</Button>
              </a>
            </div>
            <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-[#6B7280]">
              <span className="flex items-center gap-2"><CreditCard className="w-4 h-4" style={{ color: BLUE }} /> Cancel anytime</span>
              <span className="flex items-center gap-2"><Clock className="w-4 h-4" style={{ color: BLUE }} /> Setup in minutes</span>
              <span className="flex items-center gap-2"><Users className="w-4 h-4" style={{ color: BLUE }} /> Up to 80 reps</span>
            </div>
          </div>

          {/* Phone cluster */}
          <div className="flex items-end justify-center lg:justify-end mt-8 lg:mt-0" style={{ perspective: "2000px", perspectiveOrigin: "center 45%", transformStyle: "preserve-3d" }}>
            <Phone src={SCREENS.rankings} className="w-[33%] max-w-[195px]" style={{ zIndex: 1, transform: "translateZ(-150px) rotateY(30deg) translateY(16px)", transformOrigin: "bottom right", filter: "brightness(0.9)" }} />
            <Phone src={SCREENS.hq} className="w-[44%] max-w-[255px] relative" style={{ zIndex: 20, transform: "translateZ(30px)" }} />
            <Phone src={SCREENS.pit} className="w-[33%] max-w-[195px]" style={{ zIndex: 1, transform: "translateZ(-150px) rotateY(-30deg) translateY(16px)", transformOrigin: "bottom left", filter: "brightness(0.9)" }} />
          </div>
        </div>
      </section>

      {/* Everything in one place — feature rows + phones */}
      <section id="features" className="bg-[#F7F8FB] border-y border-[#EDEFF3] py-20 sm:py-24 mt-10">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="font-label tracking-[0.2em] mb-4 text-sm" style={{ color: BLUE }}>EVERYTHING YOUR TEAM NEEDS.</div>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
              <span style={{ color: INK }}>ALL IN ONE</span>{" "}
              <span style={{ color: BLUE }}>PLACE.</span>
            </h2>
          </div>

          {/* 5 app surfaces */}
          <div className="flex items-end justify-center gap-1 sm:gap-4 mb-16 pb-10 sm:pb-0 overflow-hidden" style={{ perspective: "2000px", perspectiveOrigin: "center 45%", transformStyle: "preserve-3d" }}>
            <Phone src={SCREENS.hq} className="w-[52%] max-w-[190px] sm:w-[34%]" style={{ zIndex: 1, transform: "translateZ(-260px) rotateY(40deg) translateY(26px)", transformOrigin: "bottom right", filter: "brightness(0.82)" }} />
            <Phone src={SCREENS.pit} className="w-[52%] max-w-[212px] sm:w-[42%]" style={{ zIndex: 5, transform: "translateZ(-130px) rotateY(26deg) translateY(12px)", transformOrigin: "bottom right", filter: "brightness(0.92)" }} />
            <Phone src={SCREENS.rankings} className="w-[60%] max-w-[215px] relative sm:w-[48%]" style={{ zIndex: 20, transform: "translateZ(30px)" }} />
            <Phone src={SCREENS.arena} className="w-[52%] max-w-[212px] sm:w-[42%]" style={{ zIndex: 5, transform: "translateZ(-130px) rotateY(-26deg) translateY(12px)", transformOrigin: "bottom left", filter: "brightness(0.92)" }} />
            <Phone src={SCREENS.more} className="w-[52%] max-w-[190px] sm:w-[34%]" style={{ zIndex: 1, transform: "translateZ(-260px) rotateY(-40deg) translateY(26px)", transformOrigin: "bottom left", filter: "brightness(0.82)" }} />
          </div>

          <div className="bg-white rounded-2xl border border-[#EDEFF3] shadow-[0_20px_50px_-30px_rgba(11,11,18,0.25)] px-6 sm:px-10">
            <FeatureRow icon={<Trophy className="w-7 h-7" strokeWidth={1.6} />} title="HQ" sub="COMMAND CENTER"
              desc="Your whole operation at a glance — track goals, progress, and today's standings in one home screen." />
            <FeatureRow icon={<MessagesSquare className="w-7 h-7" strokeWidth={1.6} />} title="THE PIT" sub="TEAM CHAT"
              desc="A full messaging hub — channels, DMs & group chats. Wins get recognized. People stay connected. Momentum becomes culture." />
            <FeatureRow icon={<BarChart3 className="w-7 h-7" strokeWidth={1.6} />} title="RANKINGS" sub="CLIMB THE BOARD"
              desc="Live leaderboards — daily, weekly, monthly, all-time. See who's winning, and always know where you stand." />
            <FeatureRow icon={<Swords className="w-7 h-7" strokeWidth={1.6} />} title="ARENA" sub="COMPETE LIVE"
              desc="1v1 duels, team wars, squad battles. Compete live and turn any slow day into a competition worth winning." />
            <FeatureRow icon={<LayoutGrid className="w-7 h-7" strokeWidth={1.6} />} title="MORE" sub="RUN THE ORG"
              desc="AI powered training, development, management, analytics, and the systems behind every win." />
          </div>
        </div>
      </section>

      {/* Culture — It's built */}
      <section className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-20 sm:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div>
            <div className="font-label tracking-[0.2em] mb-4 text-sm" style={{ color: BLUE }}>CULTURE ISN'T LUCK.</div>
            <h2 className="font-display text-5xl sm:text-6xl leading-[0.92] tracking-tight">
              <span style={{ color: INK }}>IT'S</span> <span style={{ color: BLUE }}>BUILT.</span>
            </h2>
            <p className="mt-6 text-lg text-[#4B5563] leading-relaxed max-w-lg">
              SalesArena builds the daily habits, healthy competition, and recognition systems that create unstoppable teams.
            </p>
          </div>
          <div className="relative">
            <img src={BOARDROOM} alt="High-performing team" className="w-full h-[340px] sm:h-[420px] object-cover rounded-2xl shadow-[0_30px_70px_-30px_rgba(11,11,18,0.5)]" />
          </div>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-x-16">
          {[
            { icon: <Flame className="w-6 h-6" strokeWidth={1.6} />, t: "RECOGNITION", s: "THAT MATTERS", d: "Every win gets seen and celebrated." },
            { icon: <Zap className="w-6 h-6" strokeWidth={1.6} />, t: "COMPETITION", s: "THAT ELEVATES", d: "Leaderboards, duels and team battles bring out the best." },
            { icon: <Rocket className="w-6 h-6" strokeWidth={1.6} />, t: "DEVELOPMENT", s: "THAT COMPOUNDS", d: "Training and coaching turn reps into future leaders." },
            { icon: <ShieldCheck className="w-6 h-6" strokeWidth={1.6} />, t: "ACCOUNTABILITY", s: "THAT SCALES", d: "Clear goals. Clear standards. Clear wins." },
          ].map((r, i) => (
            <FeatureRow key={i} icon={r.icon} title={r.t} sub={r.s} desc={r.d} />
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="bg-[#F7F8FB] border-y border-[#EDEFF3] py-20 sm:py-24">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="font-label tracking-[0.2em] mb-4 text-sm" style={{ color: BLUE }}>HOW IT WORKS</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mb-14 tracking-tight" style={{ color: INK }}>FROM SIGNUP TO #1 IN 3 STEPS.</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { n: "01", t: "CREATE YOUR ARENA", d: "Sign up your company in 60 seconds and start your 30-day free trial." },
              { n: "02", t: "INVITE YOUR REPS", d: "Add team members, set roles, customize challenges and prizes." },
              { n: "03", t: "WATCH THEM COMPETE", d: "Reps log sales, climb the board, earn badges. You see everything." },
            ].map((s, i) => (
              <div key={i} className="bg-white border border-[#EDEFF3] rounded-2xl p-8 hover:-translate-y-1 transition-transform duration-300 shadow-[0_20px_50px_-34px_rgba(11,11,18,0.3)]">
                <div className="font-display text-5xl mb-4" style={{ color: BLUE }}>{s.n}</div>
                <div className="font-display text-2xl mb-3 tracking-tight" style={{ color: INK }}>{s.t}</div>
                <p className="text-[#4B5563]">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 py-20 sm:py-24">
        <div className="mb-12">
          <div className="font-label tracking-[0.2em] mb-4 text-sm" style={{ color: BLUE }}>INVEST IN DISCIPLINE.</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight">
            <span style={{ color: INK }}>PICK YOUR</span> <span style={{ color: BLUE }}>ARENA.</span>
          </h2>
          <p className="text-[#4B5563] mt-4 text-lg max-w-2xl">Less than the cost of losing one good rep. Billed weekly. Cancel anytime. Every arena starts with a 30-day free trial.</p>
        </div>
        {trialOpen ? <PromoBanner cutoffIso={cutoffIso} /> : null}
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,400px)_1fr] gap-10 items-start">
          <div className="grid grid-cols-1 gap-6">
          {plans.map((p) => {
            const isPro = p.id === "pro";
            const isPremium = p.id === "premium";
            const highlight = isPro || isPremium;
            return (
              <div
                key={p.id}
                className={`relative bg-white rounded-2xl p-8 flex flex-col border ${isPro ? "shadow-[0_30px_60px_-24px_rgba(30,79,255,0.45)]" : isPremium ? "shadow-[0_0_0_1px_rgba(22,163,74,0.5),0_0_28px_rgba(22,163,74,0.55),0_0_60px_rgba(22,163,74,0.35),0_30px_60px_-24px_rgba(22,163,74,0.5)]" : "shadow-[0_20px_50px_-34px_rgba(11,11,18,0.3)]"}`}
                style={{ borderColor: isPro ? BLUE : isPremium ? GREEN : "#E9EBF0", borderWidth: highlight ? 2 : 1 }}
                data-testid={`plan-card-${p.id}`}
              >
                {isPro && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 text-white font-label px-4 py-1 text-xs rounded-full tracking-wider" style={{ backgroundColor: BLUE }}>
                    MOST POPULAR
                  </div>
                )}
                <div className="font-display text-3xl tracking-tight" style={{ color: INK }}>{p.label.toUpperCase()}</div>
                <div className="text-xs font-label text-[#6B7280] mb-5 mt-1 tracking-wider">{p.seat_range?.toUpperCase()}</div>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="font-display text-5xl" style={{ color: INK }}>${p.price}</span>
                  <span className="text-[#6B7280] font-label">/WEEK</span>
                </div>
                <div className="text-xs mb-6 min-h-[16px]" style={{ color: BLUE }}>
                  {p.price_yearly ? "Go with Annual — 2 months free" : ""}
                </div>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start gap-2.5 text-sm font-medium" style={{ color: INK }}>
                    <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: BLUE }} />
                    <span>{p.max_users != null ? `Up to ${p.max_users} sales reps` : "Unlimited sales reps"}</span>
                  </li>
                  {p.features.map((f, i) => {
                    const isAi = f.startsWith("AI ");
                    const isBeta = f.startsWith("Early access");
                    return (
                      <li key={i} className="flex items-start gap-2.5 text-sm text-[#374151]">
                        {isAi ? <Wand2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: BLUE }} />
                          : isBeta ? <Sparkles className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: BLUE }} />
                          : <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: BLUE }} />}
                        <span>{f}</span>
                      </li>
                    );
                  })}
                </ul>
                <Link to="/signup" data-testid={`plan-cta-${p.id}`} className="mt-auto block">
                  <Button className="w-full font-label h-12 rounded-lg text-white" style={{ backgroundColor: isPro ? BLUE : isPremium ? GREEN : "#0B0B12" }}>
                    Start with {p.label}
                  </Button>
                </Link>
                <Link to="/signup" data-testid={`plan-trial-${p.id}`} className="block mt-3">
                  <Button variant="outline" className="w-full font-label h-auto min-h-11 py-2 rounded-lg bg-white hover:bg-[#F7F8FB] border-2 whitespace-normal leading-tight text-center text-xs sm:text-sm" style={{ borderColor: isPro ? BLUE : isPremium ? GREEN : INK, color: isPro ? BLUE : isPremium ? GREEN : INK }}>
                    30-day free trial
                  </Button>
                </Link>
              </div>
            );
          })}
          </div>
          <div className="hidden lg:block" data-testid="pricing-feature-panel"><FeaturePanel /></div>
        </div>
        <p className="text-center text-sm text-[#6B7280] mt-8 max-w-2xl mx-auto">
          One plan, everything included — leaderboards, the Arena, The Pit, training, AI tools, auto-sync and integrations. 30-day free trial. Cancel anytime.
        </p>
      </section>

      {/* Final CTA */}
      <section className="bg-[#F7F8FB] border-t border-[#EDEFF3] py-20 sm:py-24">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-8 text-center">
          <div className="mx-auto mb-8 w-20 h-20 rounded-2xl flex items-center justify-center" style={{ backgroundColor: BLUE }}>
            <Trophy className="w-10 h-10 text-white" strokeWidth={1.5} />
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
            <span style={{ color: INK }}>STOP CHASING QUOTAS.</span><br />
            <span style={{ color: BLUE }}>START CHASING #1.</span>
          </h2>
          <p className="text-[#4B5563] text-lg mt-6 mb-9 max-w-2xl mx-auto">
            Join sales teams who replaced spreadsheets with a scoreboard their reps actually check.
          </p>
          <Link to="/signup" data-testid="footer-cta-signup">
            <Button size="lg" className="text-white font-label h-14 px-10 rounded-lg group" style={{ backgroundColor: BLUE }}>
              Create your Arena <ArrowRight className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" />
            </Button>
          </Link>
          <div className="text-sm text-[#6B7280] mt-4">Start your 30-day free trial · Cancel anytime.</div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#EDEFF3] py-10 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 flex items-center justify-center rounded-md" style={{ backgroundColor: BLUE }}>
              <Trophy className="w-4 h-4 text-white" />
            </div>
            <span className="font-logo text-sm whitespace-nowrap" style={{ color: INK }}>SALESARENA<span style={{ color: BLUE }}>HQ</span></span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-[#6B7280]">
            <Link to="/terms" className="hover:text-black transition-colors" data-testid="footer-link-terms">Terms</Link>
            <Link to="/privacy" className="hover:text-black transition-colors" data-testid="footer-link-privacy">Privacy</Link>
            <Link to="/refund-policy" className="hover:text-black transition-colors" data-testid="footer-link-refund">Refunds</Link>
            <a href="mailto:support@salesarenahq.com" className="hover:text-black transition-colors" data-testid="footer-link-support">Support</a>
          </div>
          <p className="text-sm text-[#6B7280]">© 2026 SALESARENA HQ LLC. Built for closers.</p>
        </div>
      </footer>
    </div>
  );
}
